import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { useStore } from "stimulus-store";
import { updateSelect } from "shared/selects/select_helpers";
import { deviceTypeFilterOptionsStore }
    from "../../../stores/testing_task_status/device_type_filter_options_store";
import { deviceTypeFilterSelectionStore }
    from "../../../stores/testing_task_status/device_type_filter_selection_store";

// Connects to data-controller="selects--testing-task-status--device-type-filter"
//
// Page-local filter widget. Its options are supplied dynamically by the page
// controller (which derives them from the devices loaded for the selected area) via
// deviceTypeFilterOptionsStore. The user's current selection is written to
// deviceTypeFilterSelectionStore, which the page controller observes to filter cards.
export default class extends Controller {

    static stores = [
        // selectionStore is both WRITTEN (in onChange) and READ (to reflect a
        // page-driven selection back onto the control after a sort). Declare it
        // FIRST so its setter is initialized before any callback can fire.
        deviceTypeFilterSelectionStore,
        deviceTypeFilterOptionsStore
    ]

    connect() {
        useStore(this);
        // Build the control synchronously so this.element.tomselect always exists for
        // every subsequent enable()/disable()/clear() call, then render whatever the
        // option store currently holds (empty on page load → disabled placeholder).
        this._buildControl();
        this._renderOptions(this.deviceTypeFilterOptionsStoreValue);
    }

    disconnect() {
        if (this.element.tomselect) {
            this.element.tomselect.destroy();
        }
    }

    onDeviceTypeFilterOptionsStoreUpdate(options) {
        this._renderOptions(options);
    }

    // Reflect a page-driven selection (e.g. restored after a sort) onto the control,
    // SILENTLY and only when it differs, so no onChange re-fires and no store↔control
    // oscillation occurs.
    onDeviceTypeFilterSelectionStoreUpdate(selectedId) {
        const control = this.element.tomselect;
        if (!control) {
            return;
        }
        const current = control.getValue() || '';
        const target = selectedId || '';
        if (current === target) {
            return;
        }
        control.setValue(target, true); // silent — does not fire onChange
    }

    _buildControl() {
        new TomSelect(this.element, {
            placeholder: 'All Device Types',
            create: false,
            plugins: ['remove_button'], // This enables a "clear" button on selected items
            valueField: 'id',
            labelField: 'name',
            searchField: 'name',
            sortField: {
                field: 'name',
                direction: 'asc'
            },
            onChange: (value) => this.setDeviceTypeFilterSelectionStoreValue(value || ''),
            maxOptions: null,
            closeAfterSelect: true
        });
    }

    _renderOptions(options) {
        const control = this.element.tomselect;
        if (!control) {
            return;
        }

        const list = Array.isArray(options) ? options : [];
        if (list.length === 0) {
            // No area data yet (or area cleared): clear + disable the control directly
            // using a SILENT clear so no onChange fires here, then write the selection
            // store exactly once.
            control.clear(true);
            control.clearOptions();
            control.disable();
            this.setDeviceTypeFilterSelectionStoreValue('');
            return;
        }
        // Non-empty: stamp real <option>s via updateSelect (which ends with a
        // non-silent setValue('')), normalize the selection store once, and enable.
        updateSelect(this.element.id, list);
        this.setDeviceTypeFilterSelectionStoreValue('');
        control.enable();
    }
}
